/**
 * MessageSearchModal — Popover compacto de busca de mensagens
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import {
  TextField,
  InputAdornment,
  Typography,
  Box,
  CircularProgress,
  Popover,
  Avatar,
} from "@material-ui/core";
import { Search, ExternalLink } from "lucide-react";
import ConnectionIcon from "../ConnectionIcon";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import debounce from "lodash/debounce";
import DOMPurify from "dompurify";
import api from "../../services/api";
import { useMessageSearch } from "../../context/MessageSearch/MessageSearchContext";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();

const CHANNEL_STYLES = {
  whatsapp: { background: "rgba(37, 211, 102, 0.08)", border: "rgba(37, 211, 102, 0.25)", color: "#25D366" },
  whatsmeow: { background: "rgba(37, 211, 102, 0.08)", border: "rgba(37, 211, 102, 0.25)", color: "#25D366" },
  wuzapi: { background: "rgba(37, 211, 102, 0.08)", border: "rgba(37, 211, 102, 0.25)", color: "#25D366" },
  waba: { background: "rgba(59, 130, 246, 0.08)", border: "rgba(59, 130, 246, 0.25)", color: "#3B82F6" },
  instagram: { background: "rgba(225, 48, 108, 0.08)", border: "rgba(225, 48, 108, 0.25)", color: "#E1306C" },
  facebook: { background: "rgba(59, 89, 152, 0.08)", border: "rgba(59, 89, 152, 0.25)", color: "#3B5998" },
  email: { background: "rgba(234, 67, 53, 0.08)", border: "rgba(234, 67, 53, 0.25)", color: "#EA4335" },
  telegram: { background: "rgba(0, 136, 204, 0.08)", border: "rgba(0, 136, 204, 0.25)", color: "#0088CC" },
  webchat: { background: "rgba(14, 165, 233, 0.08)", border: "rgba(14, 165, 233, 0.25)", color: "#0EA5E9" },
};

const getChannelStyle = (channel) =>
  CHANNEL_STYLES[channel] || { background: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.25)", color: "#6B7280" };

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    popoverPaper: {
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: dark
        ? "0 8px 32px rgba(0,0,0,0.4)"
        : "0 8px 32px rgba(0,0,0,0.12)",
      border: `1px solid ${theme.palette.divider}`,
      marginTop: theme.spacing(1),
      width: 400,
      [theme.breakpoints.down("sm")]: {
        width: 320,
      },
    },
    header: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      color: "#fff",
      padding: theme.spacing(0.875, 1.25),
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.75),
    },
    headerIcon: {
      width: 24,
      height: 24,
      borderRadius: 5,
      backgroundColor: "rgba(255,255,255,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    headerTitle: {
      fontSize: "0.8rem",
      fontWeight: 600,
      color: "#fff",
      lineHeight: 1.2,
    },
    shortcutHint: {
      fontSize: "0.65rem",
      color: "rgba(255,255,255,0.6)",
      marginLeft: "auto",
    },
    body: {
      padding: theme.spacing(1.25, 1.5, 1, 1.5),
    },
    searchField: {
      width: "100%",
      marginBottom: theme.spacing(1),
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
        fontSize: "0.85rem",
        "&.Mui-focused": {
          backgroundColor: "transparent",
        },
      },
      "& .MuiOutlinedInput-input": {
        padding: "8px 12px",
      },
    },
    resultsContainer: {
      maxHeight: 340,
      overflow: "auto",
      ...theme.scrollbarStyles,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.75),
    },
    resultCard: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1.25),
      borderRadius: 10,
      cursor: "pointer",
      transition: "all 0.15s ease",
      border: `1px solid transparent`,
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
        borderColor: alpha(theme.palette.primary.main, 0.15),
        "& $openIcon": {
          opacity: 1,
        },
      },
    },
    avatar: {
      width: 36,
      height: 36,
      fontSize: "0.8rem",
      fontWeight: 600,
      flexShrink: 0,
    },
    cardContent: {
      flex: 1,
      minWidth: 0,
    },
    cardTopRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 2,
    },
    contactName: {
      fontFamily: "'Inter', 'Roboto', 'Helvetica Neue', sans-serif",
      fontWeight: 600,
      fontSize: "0.82rem",
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flex: 1,
      minWidth: 0,
    },
    cardMeta: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.75),
      flexShrink: 0,
      marginLeft: theme.spacing(0.75),
    },
    channelBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      padding: "1px 6px",
      borderRadius: 6,
      fontSize: "0.6rem",
      fontWeight: 600,
      letterSpacing: "0.02em",
      textTransform: "capitalize",
      whiteSpace: "nowrap",
    },
    timestamp: {
      fontFamily: "'Inter', 'Roboto', 'Helvetica Neue', sans-serif",
      fontSize: "0.68rem",
      fontWeight: 500,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
    },
    highlight: {
      fontSize: "0.78rem",
      color: theme.palette.text.secondary,
      lineHeight: 1.45,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      "& mark": {
        backgroundColor: dark ? "rgba(251, 191, 36, 0.25)" : alpha(theme.palette.warning.light, 0.7),
        color: theme.palette.text.primary,
        padding: "1px 3px",
        borderRadius: 3,
        fontWeight: 600,
      },
    },
    openIcon: {
      opacity: 0,
      flexShrink: 0,
      marginTop: 2,
      color: theme.palette.text.secondary,
      transition: "opacity 0.15s ease",
    },
    loadingContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(3),
      gap: theme.spacing(1),
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: theme.spacing(3),
      color: theme.palette.text.secondary,
      gap: theme.spacing(0.5),
    },
    emptyText: {
      fontSize: "0.8rem",
      fontWeight: 500,
    },
    totalCount: {
      fontSize: "0.72rem",
      fontWeight: 500,
      color: theme.palette.text.secondary,
      padding: theme.spacing(0.25, 0, 0.5),
    },
  };
});

const MessageSearchModal = ({ open, onClose, anchorEl }) => {
  const classes = useStyles();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);

  const { setSearchTerm, setHighlightMessageId } = useMessageSearch();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const performSearch = useCallback(
    debounce(async (query) => {
      if (!query || query.trim().length < 2) {
        setResults([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      try {
        const { data } = await api.get("/messages-search", {
          params: { q: query },
          signal: abortControllerRef.current.signal,
        });

        if (isMountedRef.current) {
          setResults(data.results || []);
          setTotal(data.total || 0);
        }
      } catch (err) {
        if (err.name !== "CanceledError" && err.name !== "AbortError") {
          console.error("Erro na busca:", err);
          if (isMountedRef.current) {
            setResults([]);
            setTotal(0);
          }
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    }, 400),
    []
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      performSearch.cancel();
    };
  }, [performSearch]);

  useEffect(() => {
    performSearch(searchQuery);
  }, [searchQuery, performSearch]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setResults([]);
      setTotal(0);
    }
  }, [open]);

  const handleResultClick = (result) => {
    setSearchTerm(searchQuery);
    setHighlightMessageId(result.id);
    onClose();
    const ticketUuid = result.ticket?.uuid || result.ticketId;
    history.push(`/tickets/${ticketUuid}`);
  };

  const getAvatarUrl = (contact) => {
    if (!contact?.profilePicUrl) return null;
    const url = contact.profilePicUrl;
    if (url.startsWith("http")) return url;
    return `${backendUrl}/public/${url}`;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const clean = name.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, "").trim();
    if (!clean) return name.charAt(0) || "?";
    return clean.split(" ").filter(Boolean).map(w => w[0]).slice(0, 2).join("").toUpperCase();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      PaperProps={{ className: classes.popoverPaper }}
      TransitionProps={{
        onEntered: () => {
          if (inputRef.current) inputRef.current.focus();
        },
      }}
    >
      <div className={classes.header}>
        <div className={classes.headerIcon}>
          <Search size={14} />
        </div>
        <Typography className={classes.headerTitle}>Buscar Mensagens</Typography>
        <Typography className={classes.shortcutHint}>Ctrl+K</Typography>
      </div>

      <div className={classes.body}>
        <TextField
          inputRef={inputRef}
          className={classes.searchField}
          placeholder="Digite para buscar..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color="#9ca3af" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={20} thickness={4} />
            <Typography variant="caption" color="textSecondary">Buscando...</Typography>
          </Box>
        ) : results.length === 0 ? (
          <Box className={classes.emptyState}>
            <Search size={28} style={{ opacity: 0.15 }} />
            <Typography className={classes.emptyText}>
              {searchQuery.length >= 2 ? "Nenhum resultado" : "Digite ao menos 2 caracteres"}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography className={classes.totalCount}>
              {total} resultado{total !== 1 ? "s" : ""}
            </Typography>
            <div className={classes.resultsContainer}>
              {results.map((result) => {
                const channel = result.ticket?.channel || "whatsapp";
                const chStyle = getChannelStyle(channel);
                const contact = result.ticket?.contact;
                const avatarUrl = getAvatarUrl(contact);

                return (
                  <div
                    key={result.id}
                    className={classes.resultCard}
                    onClick={() => handleResultClick(result)}
                  >
                    <Avatar
                      className={classes.avatar}
                      src={avatarUrl}
                      imgProps={{
                        onError: (e) => { e.target.onerror = null; e.target.src = "/nopicture.png"; },
                      }}
                      style={{ backgroundColor: chStyle.color + "20", color: chStyle.color }}
                    >
                      {getInitials(contact?.name)}
                    </Avatar>

                    <div className={classes.cardContent}>
                      <div className={classes.cardTopRow}>
                        <Typography className={classes.contactName}>
                          {contact?.name || "Contato"}
                        </Typography>
                        <div className={classes.cardMeta}>
                          <span
                            className={classes.channelBadge}
                            style={{
                              backgroundColor: chStyle.background,
                              color: chStyle.color,
                              border: `1px solid ${chStyle.border}`,
                            }}
                          >
                            <ConnectionIcon connectionType={channel} style={{ fontSize: 10 }} />
                            {channel}
                          </span>
                          <Typography className={classes.timestamp}>
                            {format(new Date(result.createdAt), "dd/MM HH:mm", { locale: ptBR })}
                          </Typography>
                        </div>
                      </div>
                      <Typography
                        className={classes.highlight}
                        component="div"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            result.highlight || result.body?.substring(0, 100) || "",
                            { ALLOWED_TAGS: ["mark", "b", "em", "strong"] }
                          ),
                        }}
                      />
                    </div>

                    <ExternalLink size={13} className={classes.openIcon} />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Popover>
  );
};

export default MessageSearchModal;
